/* Webinar source -> campaign mapping UI (2026-09 round 7).
 *
 * Two exports, both backed by REAL source IDs (Meta lead form `form_key`,
 * Google Sheets connection `connection_id`, inbound webhook `endpoint_id`) -
 * never free-text source names:
 *
 *  - WebinarSourceBinding: a compact panel embedded in each source's own
 *    configuration card (Administration -> Meta lead forms, Google Sheets
 *    intake, inbound webhooks). An admin marks the source as a Webinar
 *    source and either selects an existing campaign or creates a new one
 *    (born draft + TEST) in the same governed request. A source can feed only
 *    ONE live campaign - the server answers 409 naming the current owner.
 *  - WebinarSourcePicker: the multi-select used by the campaign form, which
 *    replaces the old comma-separated "connected lead sources" text box.
 *
 * Every write is gated server-side on webinar.config; this UI only hides
 * controls the user cannot use.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errDetail } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { COMMON_TIMEZONES, DEFAULT_TIMEZONE, toneClass, useWebinarCaps } from "@/components/webinar/webinarShared";

export const sourceKey = (s) => `${s.source_type}:${s.source_id}`;
const TERMINAL = ["completed", "cancelled"];

/* Many source cards mount at once; share one in-flight request. */
let inflight = null;
export const loadWebinarBindingData = (force = false) => {
  if (inflight && !force) return inflight;
  inflight = Promise.all([
    api.get("/webinar/sources"),
    api.get("/webinar/campaigns?limit=200"),
  ]).then(([s, c]) => ({ sources: s.data.items || [], campaigns: c.data.items || [] }))
    .finally(() => { setTimeout(() => { inflight = null; }, 0); });
  return inflight;
};

/* -------------------------------------------------- campaign multi-select */
export const WebinarSourcePicker = ({ value, onChange, campaignId, disabled }) => {
  const [sources, setSources] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/webinar/sources")
      .then((r) => { if (alive) setSources(r.data.items || []); })
      .catch((e) => { if (alive) setError(errDetail(e)); });
    return () => { alive = false; };
  }, []);
  const selected = new Set(value || []);
  const toggle = (key) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key); else next.add(key);
    onChange([...next]);
  };
  if (error) return <p className="text-[11px] text-[hsl(var(--danger))]" data-testid="webinar-source-picker-error">{error}</p>;
  if (!sources) return <p className="text-[11px] text-muted-foreground">Loading sources…</p>;
  if (sources.length === 0) {
    return (
      <p className="text-[11px] text-muted-foreground" data-testid="webinar-source-picker-empty">
        No lead sources exist yet. Connect a Meta lead form, a Google Sheet or an inbound webhook first
        (Administration), then attach it here.
      </p>
    );
  }
  return (
    <ul className="rounded-md border divide-y max-h-48 overflow-y-auto" data-testid="webinar-source-picker">
      {sources.map((s) => {
        const key = sourceKey(s);
        const owner = s.campaign && !TERMINAL.includes(s.campaign.status) ? s.campaign : null;
        const takenElsewhere = !!owner && owner.campaign_id !== campaignId;
        return (
          <li key={key} className="p-1.5 flex items-start gap-2 text-xs">
            <input type="checkbox" className="mt-0.5" data-testid={`webinar-source-option-${key}`}
                   checked={selected.has(key)} disabled={disabled || (takenElsewhere && !selected.has(key))}
                   onChange={() => toggle(key)} />
            <span className="flex-1 min-w-0">
              <span className="block break-words">{s.label}</span>
              <span className="block text-[10px] text-muted-foreground">
                {s.source_type_label}{s.is_test ? " · TEST source" : ""}{s.active ? "" : " · off"}
                {takenElsewhere ? ` · already attached to “${owner.name}”` : ""}
              </span>
            </span>
          </li>
        );
      })}
    </ul>
  );
};

/* -------------------------------------------- per-source binding panel */
const emptyNew = { name: "", product_code: "", campaign_type: "live", timezone: DEFAULT_TIMEZONE, start: "" };

export const WebinarSourceBinding = ({ sourceType, sourceId, defaultProduct, onChanged }) => {
  const { canView, canConfig } = useWebinarCaps();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(null); // null | "choose" | "create"
  const [pick, setPick] = useState("");
  const [form, setForm] = useState(emptyNew);
  const [products, setProducts] = useState([]);

  const load = useCallback(async (force) => {
    try { setData(await loadWebinarBindingData(force)); setError(null); }
    catch (e) { setError(errDetail(e)); }
  }, []);
  useEffect(() => { if (canView) load(false); }, [canView, load]);
  useEffect(() => {
    if (mode === "create" && canConfig && products.length === 0) {
      api.get("/products?selectable_only=true").then((r) => setProducts(r.data || [])).catch(() => {});
    }
  }, [mode, canConfig, products.length]);

  if (!canView) return null;
  const source = data?.sources.find((s) => s.source_type === sourceType && s.source_id === sourceId);
  const owner = source?.campaign && !TERMINAL.includes(source.campaign.status) ? source.campaign : null;
  const openCampaigns = (data?.campaigns || []).filter((c) => !TERMINAL.includes(c.status));

  const link = async (body, okMessage) => {
    setBusy(true);
    try {
      await api.post("/webinar/sources/link", { source_type: sourceType, source_id: sourceId, ...body });
      toast.success(okMessage);
      setMode(null); setPick(""); setForm(emptyNew);
      await load(true);
      if (onChanged) onChanged();
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };
  const unlink = async () => {
    setBusy(true);
    try {
      await api.post("/webinar/sources/unlink", { source_type: sourceType, source_id: sourceId });
      toast.success("Source detached from its webinar campaign");
      await load(true);
      if (onChanged) onChanged();
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };
  const create = () => link({
    new_campaign: {
      name: form.name, product_code: form.product_code || defaultProduct || undefined,
      campaign_type: form.campaign_type, timezone: form.timezone,
      ...(form.campaign_type === "live" && form.start ? { scheduled_start_local: form.start } : {}),
      duration_minutes: 60,
    },
  }, "Webinar campaign created (draft, TEST) and source attached");

  const testId = `webinar-source-binding-${sourceType}-${sourceId}`;
  return (
    <div className="rounded-md border p-2 space-y-2 text-xs" data-testid={testId}>
      <div className="flex items-center gap-2 flex-wrap">
        <Switch data-testid={`${testId}-switch`} checked={!!owner || mode !== null}
                disabled={!canConfig || busy || !!error || !data}
                onCheckedChange={(on) => { if (on) setMode("choose"); else if (owner) unlink(); else setMode(null); }} />
        <span className="font-medium">Webinar source</span>
        {owner ? (
          <Badge variant="outline" data-testid={`${testId}-owner`} className={`text-[10px] ${toneClass("success")}`}>
            feeds “{owner.name}” · {owner.status}{owner.test_mode ? " · TEST" : " · LIVE"}
          </Badge>
        ) : (
          <span className="text-muted-foreground">Not a webinar source — leads are not enrolled into any webinar.</span>
        )}
        {error && <span className="text-[hsl(var(--danger))]" data-testid={`${testId}-error`}>{error}</span>}
      </div>

      {(owner || mode === "choose") && canConfig && (
        <div className="flex items-end gap-2 flex-wrap">
          <div className="space-y-1">
            <Label className="text-[11px]">{owner ? "Change campaign" : "Existing campaign"}</Label>
            <Select value={pick || undefined} onValueChange={setPick}>
              <SelectTrigger className="h-8 w-[260px] text-xs" data-testid={`${testId}-campaign`}>
                <SelectValue placeholder="Select a campaign" />
              </SelectTrigger>
              <SelectContent>
                {openCampaigns.map((c) => (
                  <SelectItem key={c.campaign_id} value={c.campaign_id}>
                    {c.name}{c.test_mode ? " · TEST" : " · LIVE"} · {c.status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button size="sm" className="h-8 text-xs" disabled={busy || !pick} data-testid={`${testId}-attach`}
                  onClick={() => link({ campaign_id: pick }, "Source attached to the webinar campaign")}>
            {owner ? "Move here" : "Attach"}
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-xs" disabled={busy}
                  data-testid={`${testId}-new`} onClick={() => setMode("create")}>
            Create new campaign
          </Button>
          {owner && (
            <Button size="sm" variant="ghost" className="h-8 text-xs" disabled={busy}
                    data-testid={`${testId}-detach`} onClick={unlink}>Detach</Button>
          )}
        </div>
      )}

      {mode === "create" && canConfig && (
        <div className="rounded-md border p-2 space-y-2 bg-muted/30" data-testid={`${testId}-create-form`}>
          <p className="text-[11px] text-muted-foreground">
            Creates a draft campaign in TEST MODE (simulated provider) and attaches this source to it.
            Go LIVE later from Webinars → Campaigns, once its readiness checks pass.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="sm:col-span-2 space-y-1">
              <Label className="text-[11px]">Campaign name</Label>
              <Input className="h-8 text-xs" data-testid={`${testId}-new-name`} value={form.name}
                     onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">Product</Label>
              <Select value={form.product_code || defaultProduct || undefined}
                      onValueChange={(v) => setForm({ ...form, product_code: v })}>
                <SelectTrigger className="h-8 text-xs" data-testid={`${testId}-new-product`}><SelectValue placeholder="Select product" /></SelectTrigger>
                <SelectContent>
                  {products.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}
                  {defaultProduct && !products.some((p) => p.code === defaultProduct) && (
                    <SelectItem value={defaultProduct}>{defaultProduct}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">Type</Label>
              <Select value={form.campaign_type} onValueChange={(v) => setForm({ ...form, campaign_type: v })}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="live">Live (one-off)</SelectItem>
                  <SelectItem value="evergreen">Evergreen (set sessions later)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">Timezone</Label>
              <Select value={form.timezone} onValueChange={(v) => setForm({ ...form, timezone: v })}>
                <SelectTrigger className="h-8 text-xs" data-testid={`${testId}-new-timezone`}><SelectValue /></SelectTrigger>
                <SelectContent>{COMMON_TIMEZONES.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.campaign_type === "live" && (
              <div className="space-y-1">
                <Label className="text-[11px]">Starts (wall-clock time in {form.timezone})</Label>
                <Input type="datetime-local" className="h-8 text-xs" data-testid={`${testId}-new-start`}
                       value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} />
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button size="sm" className="h-8 text-xs" data-testid={`${testId}-create`}
                    disabled={busy || !form.name || !(form.product_code || defaultProduct)
                              || (form.campaign_type === "live" && !form.start)}
                    onClick={create}>Create &amp; attach</Button>
            <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setMode(owner ? null : "choose")}>Back</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebinarSourceBinding;
